import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsInBreakout, selectOrderedBreakoutGroups } from "store/selectors/breakouts";
import {
  selectClassStateLastUpdatedAt,
  selectIsObservingVirtualClass,
  selectObserverBreakoutGroupId,
  selectObserverListenedBreakoutGroupId,
} from "store/selectors/pubsub-virtual-class-state";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { useListSelector } from "store/store-hooks";
import { useAvPubsubMessaging } from "utils/av";

export const useObservingVirtualClassObserver = () => {
  const dispatch = useAppDispatch();
  const { sendObservingJoinMessage } = useAvPubsubMessaging();
  const hasClassStateUpdate = !!useAppSelector(selectClassStateLastUpdatedAt);
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const observerListenedBreakoutGroupId = useAppSelector(selectObserverListenedBreakoutGroupId);
  const observerBreakoutGroupId = useAppSelector(selectObserverBreakoutGroupId);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const breakoutGroups = useListSelector(selectOrderedBreakoutGroups);

  // Send join as observer when user is observing and there is no class state
  useEffect(() => {
    if (isObservingClass && !hasClassStateUpdate) {
      sendObservingJoinMessage();
    }
  }, [isObservingClass, hasClassStateUpdate, sendObservingJoinMessage]);

  // Clear observer local listen/visit when breakouts end
  useEffect(() => {
    if (!isObservingClass) return;
    if (!isInBreakout) {
      if (observerListenedBreakoutGroupId) {
        dispatch(PubsubVirtualClassStateActions.setObserverListenedBreakoutGroupId(null));
      }
      if (observerBreakoutGroupId) {
        dispatch(PubsubVirtualClassStateActions.setObserverBreakoutGroupId(null));
      }
    }
  }, [
    dispatch,
    isInBreakout,
    isObservingClass,
    observerBreakoutGroupId,
    observerListenedBreakoutGroupId,
  ]);

  // Clear observer local listen/visit when the target group disappears
  useEffect(() => {
    if (!isObservingClass) return;
    if (
      observerListenedBreakoutGroupId &&
      !breakoutGroups.some((group) => group.id === observerListenedBreakoutGroupId)
    ) {
      dispatch(PubsubVirtualClassStateActions.setObserverListenedBreakoutGroupId(null));
    }
    if (
      observerBreakoutGroupId &&
      !breakoutGroups.some((group) => group.id === observerBreakoutGroupId)
    ) {
      dispatch(PubsubVirtualClassStateActions.setObserverBreakoutGroupId(null));
    }
  }, [
    breakoutGroups,
    dispatch,
    isObservingClass,
    observerBreakoutGroupId,
    observerListenedBreakoutGroupId,
  ]);
};
